import { createSelector } from '@app/utils/createSelector'
import moment from '@app/utils/moment'

import type { ApiUserConsent } from '@app/packages/api/v2/types/entities'
import { createLoadable, LoadRequest } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { callV2Api } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'
import { configSelector } from '@app/store/selectors/misc'
import { profileUserResultSelector } from '@app/store/selectors/profile'

import { routingDateSelector } from './routing'
import { shortSessionUuidSelector } from './short_session'

const consentsSlice = createReduxSlice<SliceState<ApiUserConsent[]>>('user_consents')

export const consentsSelector = createSelector(
  [consentsSlice.selector, shortSessionUuidSelector, profileUserResultSelector, routingDateSelector, configSelector],
  (sliceState, uuid, profileResult, date, config) =>
    createLoadable(c => {
      if (config.isPolygon) return { enabled: false, consents: [] }
      const profile = c.unwrap(profileResult)
      const consents = withCheckedSliceState(c, {
        sliceState,
        slice: consentsSlice,
        hash: [profile?.id || uuid || ''].join(':'),
        date: { date, expiresInMinutes: 24 * 60 },
        load: async dispatch => {
          const { getUserConsents } = await import('@app/packages/api/v2/actions/user_consents')
          return (await dispatch(callV2Api(getUserConsents()))).data
        },
      })
      // A pending load must reach createLoadable as-is; wrapping it in the result shape would hide it.
      if (consents instanceof LoadRequest) return consents
      return { enabled: true, consents }
    })
)

/** A brand-new session has nothing to consent to yet, so seed it empty rather than fetching. */
export const setEmptyConsents = (uuid: string) => consentsSlice.set({ hash: uuid, expires_at: moment().add(1, 'day').format(), data: [] })
